import {
  Tooltip,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { CheckCircle, HighlightOff } from '@mui/icons-material';
import DataTable from '../../../../../components/table/datatable';
import { Close, HelpOutline } from '@mui/icons-material';
import { getColor } from '../../../../../assets/theme';
import { GridColDef } from '@mui/x-data-grid';
import AddEmailModal from './fo-add-email-modal';
import { memo, useEffect, useState } from 'react';
import useMenu from '../../../../../hooks/useMenu';
import { useGetProfileDetailsQuery } from '../../../../../api/front-office/profile.api.rtk';
import { Email } from '../../../interface/fo-profile-interface';
import { ModalWrapper } from '../../../../../hooks/useModal';

const ContactsColumn: GridColDef[] = [
  {
    field: 'email',
    headerName: 'Email',
    minWidth: 500,
    hideable: false,
    sortable: false,
  },
  {
    field: 'isPrimary',
    headerName: 'Is Primary',
    renderCell: (params: any) => (
      <Stack direction='row'>
        {params.row.isPrimary ? (
          <CheckCircle style={{ color: '#22C55E' }} />
        ) : (
          <HighlightOff style={{ color: 'red' }} />
        )}
      </Stack>
    ),
    minWidth: 100,
    hideable: false,
    sortable: false,
    align: 'center',
  },
];

const EmailListModal = ({
  isOpen,
  onClose,
  profileID,
}: {
  isOpen: boolean;
  onClose: () => void;
  profileID?: string | null | undefined;
}) => {
  const { primary, textGray } = getColor();

  const [isModalOpenAddEmail, setIsModalOpenAddEmail] = useState(false);

  const {
    data: profileData,
    isError,
    isLoading,
  } = useGetProfileDetailsQuery(`/${profileID}`, {
    skip: !isOpen || !profileID,
    refetchOnMountOrArgChange: true,
  });

  const [emailData, setEmailData] = useState<Email[]>([]);

  const { handleClose } = useMenu();

  const handleOpenModalEmail = () => {
    setIsModalOpenAddEmail(true);
    handleClose();
  };

  const handleCloseModalEmail = () => {
    setIsModalOpenAddEmail(false);
  };

  const handleDrawerClose = () => {
    onClose();
  };

  useEffect(() => {
    if (!profileID || !isOpen) return;

    try {
      if (profileData?.data?.profiles?.length) {
        const profiles = profileData.data.profiles;
        profiles.forEach((profileWrapper) => {
          const profile = profileWrapper.profile;
          setEmailData(profile.listEmails || []);
        });
      } else {
        console.log('No emails found.');
      }
    } catch (error) {
      console.error('Error fetching profile emails:', error);
    }
  }, [profileData]);

  return (
    <ModalWrapper
      hideBackdrop
      disableOnClose
      onClose={onClose}
      modalDisclosure={{
        isOpen: isOpen,
        onClose: onClose,
        onToggle: () => {},
        onOpen: () => {},
      }}
      boxStyle={{
        width: '690px',
        height: '600px',
        maxHeight: '90vh',
      }}
    >
      <Box
        p={3}
        sx={{
          p: '24px',
          position: 'relative',
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          borderBottom='1px solid #EEE'
          p='10px 0'
        >
          <Typography
            variant='h5'
            component='div'
            sx={{ flexGrow: 1, color: '#000000' }}
          >
            Email List
            <Tooltip title='Learn More' placement='right-end'>
              <IconButton
                sx={{
                  border: 0,
                  color: textGray,
                  bgcolor: 'transparent',
                  ':hover': {
                    color: primary,
                    bgcolor: 'transparent',
                  },
                }}
                disableRipple
              >
                <HelpOutline />
              </IconButton>
            </Tooltip>
          </Typography>
          <IconButton color='inherit' onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <DataTable
          getRowId={(row) => row.email}
          columns={ContactsColumn}
          rows={isError ? [] : emailData}
          loading={isError ? false : isLoading}
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          pageSize={5}
          autoPageSize={false}
          checkboxSelection={false}
          tableStyle={{
            padding: 0,
            '&.MuiDataGrid-root': {
              p: 0,
            },
            '& .MuiTablePagination-root': {
              p: '0 10px',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'transparent',
              borderRadius: 0,
            },
            ".MuiButtonBase-root[aria-label='Export']": {
              display: 'none!important',
            },
            '.MuiDataGrid-root .MuiDataGrid-toolbar': {
              justifyContent: 'flex-start',
            },
          }}
          height={430}
        />
        <Stack
          direction='row'
          justifyContent='flex-end'
          borderBottom='1px solid #EEE'
        ></Stack>
        <Box mb={2} />
        <Stack direction='row' gap={1} justifyContent='flex-end'>
          <Box flexGrow={1} />
          <Button
            variant='outlined'
            sx={{
              width: '74px',
              height: '36px',
              border: '1px solid',
              backgroundColor: 'transparent',
            }}
            onClick={handleDrawerClose}
          >
            Close
          </Button>
          <Button
            type='button'
            variant='contained'
            sx={{
              width: '74px',
              height: '36px',
            }}
            onClick={(e) => {
              e.preventDefault();
              handleOpenModalEmail();
            }}
          >
            Add
          </Button>
        </Stack>
        <AddEmailModal
          isOpen={isModalOpenAddEmail}
          onClose={handleCloseModalEmail}
          profileID={profileID}
        />
      </Box>
    </ModalWrapper>
  );
};

export default memo(EmailListModal);
